
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// import { transparentStyles } from "nextus/LandingPage/MapNav/styles";

import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

import { Grid, Tabs, Tab, Box, Card, Divider, Icon } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

import { useSoftUIController, setTransparentInfo, setMiniSidenav, setHiddenSidenav, setDeviceStyle } from "context";

import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';

import WeatherProvider from "nextus/Weather";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import theme from "assets/theme";
import moment from "moment";
import SoftButton from "components/SoftButton";
import StatsPieChart from "nextus/Charts/StatisticCards/StatsPieChart";
import UptimeDoughnutChart from "nextus/Charts/StatisticCards/UptimeDoughnutChart";
import { useLocation, useNavigate } from "react-router-dom";
import SoftBadgeDot from "components/SoftBadgeDot";
import { customSidebarStyle, sidebarHeadersStyle, sidebarTabStyle, sidebarToggleStyle, exploreContentStyle } from "../styles/index";
import SidebarRoutes from "../NavRoutes";

import groupAPI from "api/groups";
import deviceAPI from "api/bks";
import userAPI from 'api/user';
import GroupLiveView from "nextus/Groups/GroupLiveView";
import { processDevicesData } from "helper/device";
import { processGroupData } from "helper/group";
import { getUserAccessLevel } from "helper/user";
import EventsBar from "modules/clearways/views/EventsBar";
import { setMiniNavbar } from "context";

const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;
const { dark, white, text, transparent } = palette;
const { navbarBoxShadow } = boxShadows;
const { rgba, pxToRem } = functions;
const { borderRadius } = borders;

function timeOfDayMessage() {
    var time_of_day = moment().format("H");

    if (time_of_day < 12) {
        return 'Good Morning';
    }
    else if (time_of_day < 17) {
        return 'Good Afternoon';
    }
    else if (time_of_day <= 23) {
        return 'Good Evening';
    }

    return 'Good Day';
}

function getGroupData(devices_data, group_data, show_inactive = false) {
    var data = {};
    var showing_all = true;

    if (group_data != null) {
        if (group_data.length != 0) {
            showing_all = false;
        }
    }

    if (showing_all) {
        var data = devices_data;
        data.name = "All Devices";
        data.devices = processDevicesData(devices_data, show_inactive);//, group_data.type);
    }
    else {
        data = processGroupData(group_data, devices_data, show_inactive);
    }

    data.showing_all = showing_all;

    return data;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, noHeight = false, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: `calc(100% - 196px)` }}
        >
            {(value === index && noHeight) && <Box>{children}</Box>}
            {(value === index && !noHeight) && <Box sx={{ height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function wrapper(content) {
    return (
        <SoftBox sx={{ height: '100%' }}>
            {content}
        </SoftBox>
    )
}

function MapNav({ sidebarOpen, setSidebarOpen, groupPreviewID = 0, sidebarState, events, eventbarState, setEventbarState }) {
    const { isLoading, user } = useAuth0();
    const [controller, dispatch] = useSoftUIController();
    const { miniSidenav, hiddenSidenav, transparentSidenav, transparentInfo, deviceStyle, miniNavbar } = controller;
    const location = useLocation();
    const { pathname } = location;
    const navigate = useNavigate();

    const closeSidenav = () => setMiniSidenav(dispatch, true);
    const toggleMiniNavbar = () => setMiniNavbar(dispatch, !miniNavbar);

    const [tabValue, setTabValue] = useState(0);

    const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // console.log('CALLING MAP NAV', sidebarState)

    deviceAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await deviceAPI.getAllDevices();
        },
        staleTime: 20 * 1000
    });

    const groups = useQuery({
        queryKey: ['getAllGroups'],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', groupPreviewID],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(groupPreviewID);
        }
    });

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        }
    });

    useEffect(() => {
        // A function that sets the mini state of the sidenav.
        function handleHiddenSidenav() {
            // setMiniSidenav(dispatch, window.innerWidth < 1200);
            setHiddenSidenav(dispatch, window.innerWidth < 1200);
            setMiniSidenav(dispatch, window.innerWidth < 1200);
            if (window.innerWidth >= 1200) {
                setMiniNavbar(dispatch, false);
            }
            setDeviceStyle(dispatch, window.innerWidth < 1200 ? "mobile" : "desktop");
        }

        // The event listener that's calling the handleMiniSidenav function when resizing the window.
        window.addEventListener("resize", handleHiddenSidenav);

        // Call the handleMiniSidenav function to set the state with the initial value.
        handleHiddenSidenav();
        // setTransparentInfo(dispatch, true);

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleHiddenSidenav);
    }, [dispatch, location]);

    function calcCollapse() {
        setSidebarOpen(!sidebarOpen);
    }

    if (groups.isLoading || devices.isLoading || selected_user.isLoading) {
        return wrapper('Loading...');
    }

    if (groups.error || devices.error || selected_user.error) {
        if (groups.error) {
            var error = groups.error;
        }
        else if (devices.error) {
            var error = devices.error;
        }
        else if (selected_user.error) {
            var error = selected_user.error;
        }
        return wrapper("An error has occurred: " + error.message);
    }

    if (!devices.data || !selected_user.data) {
        return wrapper('Loading...');
    }

    var group_data = getGroupData(devices.data, group.data, getUserAccessLevel(selected_user.data.user.user_type, 'technician'));

    var hot_pink = selected_user.data.user.email == 'amy.lindsay@transport.nsw.gov.au';

    var position = { latitude: 0, longitude: 0 };

    try {
        var lat = devices.data.map((element) => element.position.latitude);
        position.latitude = lat.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        var lng = devices.data.map((element) => element.position.longitude);
        position.longitude = lng.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        if (isNaN(position.latitude) || isNaN(position.longitude)) {
            var position = { latitude: 33.8688, longitude: 151.2093 };
        }
    }
    catch (e) {
        console.log('improper device data provided');
    }

    const colours = ['success', 'warning', 'error'];
    const renderBadgeDots = ['Full', 'Healthy', 'Low'].map((label, index) => {
        return (
            <SoftBadgeDot
                key={`badge-dot-${index}`}
                variant="gradient"
                color={colours[index]}
                size="lg"
                badgeContent={label}
                font={{ color: "text", weight: "medium" }}
                px={0}
            />
        );
    });

    var liveview_cards = groups.data.filter(row => (groupPreviewID == 0 || row.parent_id == groupPreviewID || row.group_id == groupPreviewID) && row.type == "clearways").map(function (row) {
        var ret = processGroupData(row, devices.data);
        return ret;
    }).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }).map(function (row) {
        if (row.devices.length > 0) {
            return (
                <Grid item key={row.group_id} xxs={12} md={6} xl={12}>
                    <Card sx={{ paddingBottom: '16px', height: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Grid container>
                            <Grid item xxs={12}>
                                <SoftBox sx={{ height: '100%', width: '100%' }} >
                                    <SoftBox sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                        <GroupLiveView group={row} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={12}>
                                <SoftBox pb={2} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', textAlign: 'center' }}>
                                    <SoftTypography variant={'h4'} textTransform='capitalize' fontWeight='bold'>
                                        {row.name}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            );
        }
    }).filter(row => typeof row != 'undefined');

    var renderSidebarItems = {};
    if (pathname.includes('explore')) {
        renderSidebarItems = (
            <SoftBox height={'100%'}>
                <Grid container>
                    <Grid item xxs={12}>
                        <Grid container mt={0.5} sx={(theme) => sidebarHeadersStyle(theme, { sidebarState })}>
                            <Grid item xxs={12} textAlign={'center'}>
                                <SoftBox className="subheading">
                                    {group_data.name == "All Devices" && (
                                        <SoftTypography>
                                            Showing {group_data.name}. Click a polygon to view its group details.
                                        </SoftTypography>
                                    )}
                                    {(group_data.name != "All Devices" && group_data.subgroups.length > 0) && (
                                        <SoftTypography>
                                            Showing {group_data.name}. Click a subgroup line to view its devices.
                                        </SoftTypography>
                                    )}
                                    {(group_data.name != "All Devices" && group_data.subgroups.length == 0) && (
                                        <SoftTypography>
                                            Showing {group_data.name}. Click a device point to view more.
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xxs={12} sm={12} my={{ xxs: 2, sm: 2 }} mx={{ xxs: 1, sm: 1 }}>
                        <Grid container>
                            <Grid item xxs={deviceStyle == 'mobile' ? 6 : 12} p={1} sx={{ paddingTop: '0px !important' }}>
                                <SoftButton fullWidth onClick={(e) => { navigate('/') }}>Return to Menu</SoftButton>
                            </Grid>
                            {deviceStyle == 'mobile' && (
                                <Grid item xxs={6} p={1} sx={{ paddingTop: '0px !important' }}>
                                    <SoftButton fullWidth onClick={toggleMiniNavbar}>Toggle Menu</SoftButton>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xxs={12} sm={12} mx={{ xxs: 2, sm: 2 }} mb={{ xxs: 2, sm: miniNavbar ? 0 : 2 }} sx={{ width: '-webkit-fill-available' }}>
                        <Tabs value={tabValue}
                            onChange={handleTabClick} aria-label="MapNav Tabs"
                            allowScrollButtonsMobile
                            variant="scrollable"
                            scrollButtons
                            sx={{
                                display: miniNavbar ? 'none' : 'flex',
                                opacity: 0.8,
                                [`& .${tabsClasses.scrollButtons}`]: {
                                    '&.Mui-disabled': { opacity: 0.3 },
                                },
                            }}
                        >
                            <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Liveviews" {...a11yProps(0)} />
                            <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Statistics" {...a11yProps(1)} />
                            {deviceStyle == 'mobile' && (
                                <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Upcoming Events" {...a11yProps(2)} />
                            )}
                        </Tabs>
                    </Grid>
                </Grid>
                <SoftBox height={'100%'}>
                    <CustomTabPanel value={tabValue} index={0}>
                        <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                            <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { sidebarState, miniNavbar })}>
                                <SoftBox px={2}
                                    sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                                        height: 'auto',
                                        width: '-webkit-fill-available',
                                        overflowY: 'auto',
                                        '::-webkit-scrollbar': {
                                            backgroundColor: 'transparent',
                                            width: '6px',
                                        },
                                        '::-webkit-scrollbar-track': {
                                            backgroundColor: 'transparent',
                                        },
                                        '::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#babac0',
                                            borderRadius: '6px',
                                        },
                                        '::-webkit-scrollbar-button': {
                                            display: 'none',
                                        },
                                    })}
                                >
                                    {liveview_cards.length > 0 && (
                                        <SoftBox sx={{ overflow: 'visible', height: '100%' }}>
                                            <Grid container spacing={3}>
                                                {liveview_cards}
                                            </Grid>
                                        </SoftBox>
                                    )}
                                    {liveview_cards.length == 0 && (
                                        <SoftBox display='flex' flexDirection='column' justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
                                            <SoftTypography variant={window.innerWidth < breakpoints.up("sm") ? "h6" : "h5"}>
                                                No Previews Available!
                                            </SoftTypography>
                                        </SoftBox>
                                    )}
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                            <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { sidebarState, miniNavbar })}>
                                <SoftBox px={1}
                                    sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                                        height: 'auto',
                                        overflowY: 'auto',
                                        '::-webkit-scrollbar': {
                                            backgroundColor: 'transparent',
                                            width: '6px',
                                        },
                                        '::-webkit-scrollbar-track': {
                                            backgroundColor: 'transparent',
                                        },
                                        '::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#babac0',
                                            borderRadius: '6px',
                                        },
                                        '::-webkit-scrollbar-button': {
                                            display: 'none',
                                        },
                                    })}
                                >
                                    <SoftBox sx={{ overflow: 'visible', height: '100%' }}>
                                        <Grid container>
                                            {/* <Grid item xxs={12} p={1}>
                                                <UptimeDoughnutChart
                                                    icon="percent"
                                                    color="secondary"
                                                    title={group_data.name}
                                                    sidebarState={sidebarState}
                                                    devices={group_data.devices}
                                                />
                                            </Grid> */}
                                            <Grid item xxs={12} p={1}>
                                                <Card>
                                                    <SoftBox display="flex" flexDirection="row" justifyContent={'space-evenly'}>
                                                        {renderBadgeDots}
                                                    </SoftBox>
                                                </Card>
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12} p={1}>
                                                <StatsPieChart
                                                    title="Battery Levels"
                                                    devices={group_data.devices}
                                                    data_type={"battery"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12} p={1}>
                                                <StatsPieChart
                                                    title="Signal Levels"
                                                    devices={group_data.devices}
                                                    data_type={"rssi"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12} p={1}>
                                                <StatsPieChart
                                                    title="Temperature Levels"
                                                    devices={group_data.devices}
                                                    data_type={"temperature"}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} md={6} xl={12} p={1}>
                                                <StatsPieChart
                                                    title="Humidity Levels"
                                                    devices={group_data.devices}
                                                    data_type={"humidity"}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </CustomTabPanel>
                    {deviceStyle == 'mobile' && (
                        <CustomTabPanel value={tabValue} index={2}>
                            <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                                <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { sidebarState, miniNavbar })}>
                                    <EventsBar events_data={events} eventbarState={eventbarState} setEventbarState={setEventbarState} sidebarState={sidebarState} />
                                </Grid>
                            </Grid>
                        </CustomTabPanel>
                    )}
                </SoftBox>
            </SoftBox>
        )
    }
    else {
        renderSidebarItems = (
            <SoftBox py={3} px={{ xxs: 0, xs: 3 }} height={{ xxs: 'calc(100svh - 96px)', xs: 'calc(100svh - 50px)' }}
                sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                    overflow: 'auto',

                    '::-webkit-scrollbar': {
                        backgroundColor: 'transparent',
                        width: '6px',
                    },
                    '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
                        backgroundColor: 'transparent',
                    },
                    '::-webkit-scrollbar-thumb': { /* scrollbar itself */
                        backgroundColor: '#babac0',
                        borderRadius: '6px',
                        // border: `1px solid ${rgba(white.main, 0.8)}`,
                    },
                    '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
                        display: 'none',
                    },
                })}
            >
                <SoftBox sx={{ overflow: 'visible', height: '100%' }}>
                    <Grid container spacing={3} pb={0.5}>
                        <Grid item xxs={12}>
                            <Grid container sx={(theme) => sidebarHeadersStyle(theme, { sidebarState })}>
                                <Grid item xxs={12} textAlign={'center'}>
                                    <SoftBox mb={1} className="userWelcome">
                                        <SoftTypography>
                                            {timeOfDayMessage()} {user.nickname}!
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xxs={12}>
                            <SidebarRoutes sidebarState={sidebarState} />
                        </Grid>
                    </Grid>
                    <Divider />
                    <Grid container spacing={2}>
                        {/* <Grid item xxs={12}>
                            <UptimeDoughnutChart
                                icon="percent"
                                color="secondary"
                                title="All Devices"
                                sidebarState={sidebarState}
                                devices={group_data.devices}
                            />
                        </Grid> */}
                        <Grid item xxs={12} pb={2}>
                            <WeatherProvider position={position} icon_width={'50%'} />
                        </Grid>
                    </Grid>
                    {deviceStyle == 'mobile' && (
                        <Grid container spacing={2}>
                            <Grid item xxs={12} pb={2}>
                                <EventsBar events_data={events} eventbarState={eventbarState} setEventbarState={setEventbarState} sidebarState={sidebarState} />
                            </Grid>
                        </Grid>
                    )}
                </SoftBox>
            </SoftBox>
        );
    }

    if (sidebarState == 'landing') {
        return (wrapper(
            <Grid container>
                <Grid item xxs={12} sm={12} md={12} lg={12} xl={5} xxl={5} x3l={5} x4l={4}>
                    <Grid container flexWrap={"nowrap"} sx={(theme) => customSidebarStyle(theme, { transparentInfo, sidebarState, deviceStyle })}>
                        <Grid item xxs={12} xl={11} position={"relative"} left={0} top={0} id="sidebar" sx={(theme) => sidebarTabStyle(theme, { transparentInfo, sidebarState, hot_pink, deviceStyle })}>
                            {renderSidebarItems}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
        );
    }
    else if (sidebarState == 'exploring') {
        return (wrapper(
            <Grid container sx={{ height: '100%' }}>
                <Grid item xxs={12} sm={12} md={12} lg={12} xl={5} xxl={5} x3l={5} x4l={4} sx={{ height: 'inherit' }}>
                    <Grid container flexWrap={"nowrap"} sx={(theme) => customSidebarStyle(theme, { transparentInfo, sidebarState, deviceStyle, miniNavbar })}>
                        <Grid item xxs={12} xl={11} position={"relative"} left={0} top={0} id="sidebar" sx={(theme) => sidebarTabStyle(theme, { transparentInfo, sidebarState, hot_pink, deviceStyle })}>
                            {renderSidebarItems}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
        );
    }
    else { //default
        return (
            <Grid container sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <Grid item xxs={12} sm={10} md={6} lg={5} xl={5} xxl={5} x3l={5} x4l={4} display={'inline-flex'} flexDirection={'row'} alignItems={'center'}>
                    <Grid container flexWrap={"nowrap"} sx={(theme) => customSidebarStyle(theme, { transparentInfo, sidebarState, deviceStyle })}>
                        <Grid item xxs={11} position={"relative"} left={0} top={0} id="sidebar" sx={(theme) => sidebarTabStyle(theme, { transparentInfo, sidebarState, hot_pink, deviceStyle })}>
                            {renderSidebarItems}
                        </Grid>
                        <Grid item id="sidebar-bar" position={'relative'} xxs={1} left={30} alignSelf={'center'} onClick={calcCollapse} sx={(theme) => sidebarToggleStyle(theme, { sidebarState })}>
                            {sidebarOpen == true && (<KeyboardDoubleArrowLeftIcon fontSize="large" />)}
                            {sidebarOpen == false && (<KeyboardDoubleArrowRightIcon fontSize="large" />)}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }


}

MapNav.propTypes = {
    sidebarOpen: PropTypes.bool,
    setSidebarOpen: PropTypes.func,
    groupPreviewID: PropTypes.number || PropTypes.string,
    sidebarState: PropTypes.string,
    events: PropTypes.array,
    eventbarState: PropTypes.string,
    setEventbarState: PropTypes.func,
}

export default MapNav;
